import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { User, Mail, Play, X, Check, Link } from 'lucide-react';
import { supabase } from '@/lib/supabase';

export function AssignmentModal({ open, onOpenChange, projectId, task, onSaveAssignees, webhookVariables }) {
  const [spaceUsers, setSpaceUsers] = useState([]);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [loading, setLoading] = useState(true);

  // Get form fields that can be used for dynamic assignment
  const emailFields = task?.content?.filter(item => item.type === 'Email') || [];
  const userFields = task?.content?.filter(item => item.type === 'User') || [];

  useEffect(() => {
    if (open && projectId) {
      fetchSpaceUsers();
    }
  }, [open, projectId]);

  // Load existing assignees when modal opens
  useEffect(() => {
    if (open && task?.assignees) {
      setSelectedAssignees(task.assignees);
    }
  }, [open, task]);

  const fetchSpaceUsers = async () => {
    setLoading(true);
    try {
      // First, try to fetch team members from the project
      const { data: teamData, error: teamError } = await supabase
        .from('team_members')
        .select(`
          user_id,
          user_profiles!inner(
            id,
            first_name,
            last_name,
            avatar_url,
            email
          )
        `)
        .eq('project_id', projectId);

      if (teamError) throw teamError;

      // If project has team members, use those
      if (teamData && teamData.length > 0) {
        const users = teamData.map(member => ({
          id: member.user_profiles.id,
          first_name: member.user_profiles.first_name,
          last_name: member.user_profiles.last_name,
          avatar_url: member.user_profiles.avatar_url,
          email: member.user_profiles.email,
        }));
        setSpaceUsers(users);
      } else {
        // Fall back to company members if no project team members
        // First get the project's company_id
        const { data: projectData, error: projectError } = await supabase
          .from('projects')
          .select('company_id')
          .eq('id', projectId)
          .single();

        if (projectError) throw projectError;

        // Fetch company members
        const { data: companyData, error: companyError } = await supabase
          .from('company_members')
          .select(`
            user_id,
            user_profiles!inner(
              id,
              first_name,
              last_name,
              avatar_url,
              email
            )
          `)
          .eq('company_id', projectData.company_id);

        if (companyError) throw companyError;

        const users = companyData.map(member => ({
          id: member.user_profiles.id,
          first_name: member.user_profiles.first_name,
          last_name: member.user_profiles.last_name,
          avatar_url: member.user_profiles.avatar_url,
          email: member.user_profiles.email,
        }));

        setSpaceUsers(users);
      }
    } catch (error) {
      console.error('Error fetching space users:', error);
    } finally {
      setLoading(false);
    }
  };

  const matchAssignee = (a, b) => {
    if (a.type !== b.type) return false;
    if (b.type === 'user') return a.user_id === b.user_id;
    if (b.type === 'webhook_field') return a.webhookKey === b.webhookKey;
    return a.field_id === b.field_id;
  };

  const toggleAssignee = (assignee) => {
    const exists = selectedAssignees.find(a => matchAssignee(a, assignee));
    if (exists) {
      setSelectedAssignees(selectedAssignees.filter(a => !matchAssignee(a, assignee)));
    } else {
      setSelectedAssignees([...selectedAssignees, assignee]);
    }
  };

  const isSelected = (assignee) => {
    return selectedAssignees.some(a => matchAssignee(a, assignee));
  };

  const handleSave = () => {
    onSaveAssignees(selectedAssignees);
    onOpenChange(false);
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const getUserDisplay = (userId) => {
    const user = spaceUsers.find(u => u.id === userId);
    if (!user) return null;
    return `${user.first_name} ${user.last_name}`;
  };

  const getFieldDisplay = (fieldId) => {
    const field = [...emailFields, ...userFields].find(f => f.id === fieldId);
    return field?.label || 'Unnamed Field';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Assign Task
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          {/* Selected Assignees Preview */}
          {selectedAssignees.length > 0 && (
            <div className="mb-6 p-4 bg-muted/50 rounded-lg">
              <div className="text-sm font-medium mb-2">Selected Assignees</div>
              <div className="flex flex-wrap gap-2">
                {selectedAssignees.map((assignee, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 px-3 py-1.5 bg-background border rounded-full text-sm"
                  >
                    {assignee.type === 'workflow_runner' && (
                      <>
                        <Play className="h-3 w-3" />
                        <span>Workflow Runner</span>
                      </>
                    )}
                    {assignee.type === 'user' && (
                      <>
                        <User className="h-3 w-3" />
                        <span>{getUserDisplay(assignee.user_id)}</span>
                      </>
                    )}
                    {assignee.type === 'email_field' && (
                      <>
                        <Mail className="h-3 w-3" />
                        <span>{getFieldDisplay(assignee.field_id)}</span>
                      </>
                    )}
                    {assignee.type === 'user_field' && (
                      <>
                        <User className="h-3 w-3" />
                        <span>{getFieldDisplay(assignee.field_id)}</span>
                      </>
                    )}
                    {assignee.type === 'webhook_field' && (
                      <>
                        <Link className="h-3 w-3" />
                        <span>{assignee.webhookKey?.replace(/^webhook\./, '')}</span>
                      </>
                    )}
                    <button
                      onClick={() => toggleAssignee(assignee)}
                      className="text-muted-foreground hover:text-foreground ml-1"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dynamic Assignees Section */}
          <div className="mb-6">
            <div className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">
              Dynamic Assignees
            </div>
            <div className="space-y-2">
              {/* Workflow Runner */}
              <button
                onClick={() => toggleAssignee({ type: 'workflow_runner' })}
                className={`w-full flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors text-left ${
                  isSelected({ type: 'workflow_runner' }) ? 'bg-primary/5 border-primary' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100">
                    <Play className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-medium">Workflow Runner</div>
                    <div className="text-xs text-muted-foreground">
                      Person who starts the workflow
                    </div>
                  </div>
                </div>
                {isSelected({ type: 'workflow_runner' }) && (
                  <Check className="h-5 w-5 text-primary" />
                )}
              </button>

              {/* Email Fields */}
              {emailFields.map((field) => (
                <button
                  key={field.id}
                  onClick={() => toggleAssignee({ type: 'email_field', field_id: field.id })}
                  className={`w-full flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors text-left ${
                    isSelected({ type: 'email_field', field_id: field.id }) ? 'bg-primary/5 border-primary' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange-100">
                      <Mail className="h-4 w-4 text-orange-600" />
                    </div>
                    <div>
                      <div className="font-medium">{field.label || 'Email Field'}</div>
                      <div className="text-xs text-muted-foreground">
                        Email address from form field
                      </div>
                    </div>
                  </div>
                  {isSelected({ type: 'email_field', field_id: field.id }) && (
                    <Check className="h-5 w-5 text-primary" />
                  )}
                </button>
              ))}

              {/* User Fields */}
              {userFields.map((field) => (
                <button
                  key={field.id}
                  onClick={() => toggleAssignee({ type: 'user_field', field_id: field.id })}
                  className={`w-full flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors text-left ${
                    isSelected({ type: 'user_field', field_id: field.id }) ? 'bg-primary/5 border-primary' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-100">
                      <User className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium">{field.label || 'User Field'}</div>
                      <div className="text-xs text-muted-foreground">
                        User selected from form field
                      </div>
                    </div>
                  </div>
                  {isSelected({ type: 'user_field', field_id: field.id }) && (
                    <Check className="h-5 w-5 text-primary" />
                  )}
                </button>
              ))}

              {emailFields.length === 0 && userFields.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-4 border rounded-lg">
                  Add Email or User fields to the form to enable dynamic assignment
                </div>
              )}
            </div>
          </div>

          {/* Webhook Variables Section */}
          {webhookVariables && webhookVariables.length > 0 && (
            <div className="mb-6">
              <div className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">
                Webhook
              </div>
              <div className="space-y-2">
                {webhookVariables.map((variable) => {
                  const key = variable.key;
                  const keyLower = key.toLowerCase();
                  const looksLikeId = keyLower.endsWith('.id') || keyLower.endsWith('_id') || keyLower.endsWith('user_id');
                  const looksLikeEmail = keyLower.includes('email');
                  const matchBy = looksLikeId ? 'user_id' : 'email';
                  const matchLabel = looksLikeId
                    ? 'Will match user by ID'
                    : looksLikeEmail
                      ? 'Will match user by email address'
                      : 'Will try to match user by email';
                  const assignee = { type: 'webhook_field', webhookKey: key, matchBy };

                  return (
                    <button
                      key={key}
                      onClick={() => toggleAssignee(assignee)}
                      className={`w-full flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors text-left ${
                        isSelected(assignee) ? 'bg-primary/5 border-primary' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-green-100">
                          <Link className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <div className="font-medium">{variable.key}</div>
                          <div className="text-xs text-muted-foreground">
                            {matchLabel}
                          </div>
                        </div>
                      </div>
                      {isSelected(assignee) && (
                        <Check className="h-5 w-5 text-primary" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Users Section */}
          <div>
            <div className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">
              Users
            </div>
            {loading ? (
              <div className="text-sm text-muted-foreground text-center py-8">
                Loading users...
              </div>
            ) : spaceUsers.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-8 border rounded-lg">
                No users found in this space
              </div>
            ) : (
              <div className="space-y-2">
                {spaceUsers.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => toggleAssignee({ type: 'user', user_id: user.id })}
                    className={`w-full flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors text-left ${
                      isSelected({ type: 'user', user_id: user.id }) ? 'bg-primary/5 border-primary' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user.avatar_url} alt={`${user.first_name} ${user.last_name}`} />
                        <AvatarFallback>{getInitials(user.first_name, user.last_name)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{user.first_name} {user.last_name}</div>
                        <div className="text-xs text-muted-foreground">{user.email}</div>
                      </div>
                    </div>
                    {isSelected({ type: 'user', user_id: user.id }) && (
                      <Check className="h-5 w-5 text-primary" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer with Save Button */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Assignees
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
